import { useEffect, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { StatCard } from '../components/StatCard'

/**
 * Developer key budget requests: the admin side. Maroun's own addition to
 * the feature -- "this needs an audit trail for the user and admin" -- is
 * why every request row expands to its full `events` timeline rather than
 * just a decide button; the decision itself is `budget_request`'s current
 * state, but WHO decided, WHEN, and whether the gateway write actually
 * landed only exist in that timeline (`clients/budgetRequests.ts`, backend).
 */

export interface BudgetRequestEvent {
  id: number
  requestId: number
  at: string
  actorEmail: string
  actorRole: 'user' | 'admin'
  action: 'created' | 'withdrawn' | 'approved' | 'rejected' | 'budget_applied' | 'apply_failed' | 'notified' | 'notify_failed'
  fromStatus: string | null
  toStatus: string | null
  requestedBudgetUsd: number | null
  approvedBudgetUsd: number | null
  note: string | null
}

export interface BudgetRequest {
  id: number
  email: string
  /** Missing on any row written before 25 Sep 2026 -- read as 'devkey',
   *  the only thing this table meant before a chat-ceiling request existed. */
  kind?: 'devkey' | 'chat'
  keyAlias: string
  currentBudgetUsd: number | null
  requestedBudgetUsd: number
  justification: string
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn'
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  approvedBudgetUsd: number | null
  createdAt: string
  events: BudgetRequestEvent[]
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

function fmtMoney(n: number | null): string {
  return n === null ? '-' : `$${n.toFixed(2)}`
}

function fmtDate(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const ACTION_LABEL: Record<BudgetRequestEvent['action'], string> = {
  created: 'Submitted',
  withdrawn: 'Withdrawn',
  approved: 'Approved',
  rejected: 'Rejected',
  budget_applied: 'Budget applied on the gateway',
  apply_failed: 'Gateway apply FAILED',
  notified: 'Person notified by email',
  notify_failed: 'Notification email FAILED',
}

const STATUS_TONE: Record<BudgetRequest['status'], { bg: string; fg: string }> = {
  pending: { bg: '#FFF4D6', fg: '#8A6D00' },
  approved: { bg: '#D9F2E4', fg: '#0D6B3F' },
  rejected: { bg: '#FBDCE0', fg: '#9A1F35' },
  withdrawn: { bg: '#E7E7E7', fg: '#5C5C5C' },
}

function StatusBadge({ status, tokens }: { status: BudgetRequest['status']; tokens: ThemeTokens }) {
  const tone = STATUS_TONE[status]
  const styles = pageStyles(tokens)
  return <span style={{ ...styles.badge, background: tone.bg, color: tone.fg }}>{status}</span>
}

/** 'Chat' or 'Key' -- which ceiling this row is about, since the mock's own
 *  admin queue row shows James Makin (a Chat request) beside developer-key
 *  requests in the same table. Missing `kind` (every row before 25 Sep
 *  2026) reads as 'Key', the only thing this table ever meant before. */
function KindPill({ kind, tokens }: { kind: BudgetRequest['kind']; tokens: ThemeTokens }) {
  const isChat = kind === 'chat'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.15rem 0.55rem',
        borderRadius: '9999px',
        fontSize: '0.72rem',
        fontWeight: 700,
        background: isChat ? '#E7F2FF' : tokens.border,
        color: isChat ? '#255A91' : tokens.textPrimary,
      }}
    >
      {isChat ? 'Chat' : 'Key'}
    </span>
  )
}

/** The timeline: one line per event, oldest first, exactly the order
 *  `listEvents` (backend) already returns. Every event carries who and
 *  when; a failed apply or a failed notification prints its own message
 *  rather than being indistinguishable from a success. */
function Timeline({ events, tokens }: { events: BudgetRequestEvent[]; tokens: ThemeTokens }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {events.map((e) => {
        const failed = e.action === 'apply_failed' || e.action === 'notify_failed'
        return (
          <li key={e.id} style={{ fontSize: '0.82rem', color: failed ? tokens.danger : tokens.textPrimary, borderLeft: `2px solid ${failed ? tokens.danger : tokens.border}`, paddingLeft: '0.7rem' }}>
            <div>
              <b>{ACTION_LABEL[e.action]}</b>{' '}
              <span style={{ color: tokens.textSecondary }}>
                by {e.actorEmail} ({e.actorRole}), {fmtDate(e.at)}
              </span>
            </div>
            {e.approvedBudgetUsd !== null ? <div style={{ color: tokens.textSecondary }}>Budget: {fmtMoney(e.approvedBudgetUsd)}</div> : null}
            {e.note ? <div style={{ color: tokens.textSecondary, whiteSpace: 'pre-wrap' }}>{e.note}</div> : null}
          </li>
        )
      })}
    </ol>
  )
}

function DecisionForm({
  request,
  tokens,
  busy,
  onApprove,
  onReject,
}: {
  request: BudgetRequest
  tokens: ThemeTokens
  busy: boolean
  onApprove: (approvedBudget: number, note: string) => void
  onReject: (reason: string) => void
}) {
  const styles = pageStyles(tokens)
  const [approvedBudget, setApprovedBudget] = useState(String(request.requestedBudgetUsd))
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  return (
    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.8rem' }}>
      <div style={{ flex: '1 1 16rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: tokens.textPrimary, marginBottom: '0.4rem' }}>Approve</div>
        <input
          type="number"
          value={approvedBudget}
          onChange={(e) => setApprovedBudget(e.target.value)}
          style={{ ...styles.input, width: '100%', marginBottom: '0.4rem' }}
        />
        <textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={styles.textarea} />
        <button
          type="button"
          disabled={busy}
          onClick={() => onApprove(Number(approvedBudget), note)}
          style={{ ...styles.button, marginTop: '0.5rem' }}
        >
          Approve
        </button>
      </div>
      <div style={{ flex: '1 1 16rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: tokens.textPrimary, marginBottom: '0.4rem' }}>Reject</div>
        <textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={styles.textarea} />
        <button
          type="button"
          disabled={busy || reason.trim().length === 0}
          onClick={() => onReject(reason.trim())}
          style={{ ...styles.buttonDanger, marginTop: '0.5rem' }}
        >
          Reject
        </button>
      </div>
    </div>
  )
}

export function BudgetRequestsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = pageStyles(tokens)
  const [requests, setRequests] = useState<BudgetRequest[]>([])
  const [history, setHistory] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  async function load(showHistory: boolean) {
    setLoading(true)
    setError(null)
    try {
      const result = await apiJson<{ requests: BudgetRequest[] }>(`/api/budget-requests${showHistory ? '?history=1' : ''}`)
      setRequests(result.requests)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(history)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  async function approve(id: number, approvedBudget: number, note: string) {
    setBusyId(id)
    setError(null)
    try {
      await apiJson(`/api/budget-requests/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvedBudget, note: note || undefined }),
      })
      await load(history)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  async function reject(id: number, reason: string) {
    setBusyId(id)
    setError(null)
    try {
      await apiJson(`/api/budget-requests/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      })
      await load(history)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  const columns: Array<DataTableColumn<BudgetRequest>> = [
    { key: 'email', header: 'Person', render: (r) => r.email },
    { key: 'kind', header: 'Ceiling', render: (r) => <KindPill kind={r.kind} tokens={tokens} /> },
    // A chat request has no key: the alias column is blank rather than
    // printing an empty string that reads as a rendering bug.
    { key: 'alias', header: 'Alias', render: (r) => (r.kind === 'chat' ? '-' : r.keyAlias) },
    { key: 'current', header: 'Now', align: 'right', render: (r) => fmtMoney(r.currentBudgetUsd) },
    { key: 'requested', header: 'Requested', align: 'right', render: (r) => fmtMoney(r.requestedBudgetUsd) },
    {
      key: 'justification',
      header: 'Justification',
      render: (r) => (
        <span title={r.justification} style={{ display: 'inline-block', maxWidth: '18rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.justification}
        </span>
      ),
    },
    { key: 'submitted', header: 'Submitted', render: (r) => fmtDate(r.createdAt) },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} tokens={tokens} /> },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.statsRow}>
        <StatCard label="Pending" value={history ? '-' : requests.length} tokens={tokens} />
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Budget requests</h2>
            <p style={styles.sectionSubtitle}>
              A higher developer-key budget is not self-service: every request lands here with the person's own justification,
              and every decision (and whether it actually reached the key) is recorded below the row.
            </p>
          </div>
          <div style={styles.toolbar}>
            <button type="button" onClick={() => setHistory(false)} style={history ? styles.buttonGhost : styles.button}>
              Pending
            </button>
            <button type="button" onClick={() => setHistory(true)} style={history ? styles.button : styles.buttonGhost}>
              History
            </button>
          </div>
        </div>

        {error ? <p style={styles.error}>{error}</p> : null}
        {loading ? (
          <p style={styles.hint}>Loading...</p>
        ) : (
          <DataTable
            columns={columns}
            rows={requests}
            getRowKey={(r) => String(r.id)}
            getRowLabel={(r) => r.email}
            tokens={tokens}
            emptyLabel={history ? 'No decided requests.' : 'No pending requests.'}
            onRowActivate={(r) => setExpandedId((cur) => (cur === r.id ? null : r.id))}
            isRowExpanded={(r) => expandedId === r.id}
            renderExpanded={(r) => (
              <div>
                <Timeline events={r.events} tokens={tokens} />
                {r.status === 'pending' ? (
                  <DecisionForm
                    request={r}
                    tokens={tokens}
                    busy={busyId === r.id}
                    onApprove={(approvedBudget, note) => void approve(r.id, approvedBudget, note)}
                    onReject={(reason) => void reject(r.id, reason)}
                  />
                ) : null}
              </div>
            )}
          />
        )}
      </div>
    </div>
  )
}
